import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge } from "@/components/ui-parts";
import { Send, X, MessageSquare, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function MissingReceipts() {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkMember, setBulkMember] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyFilter) params.company_id = companyFilter;
      const [{ data: mr }, { data: t }, { data: c }] = await Promise.all([
        api.get("/missing-receipts", { params }),
        api.get("/team"),
        api.get("/companies"),
      ]);
      setItems(mr);
      setTeam(t.filter((m) => !m.is_owner && m.telegram_linked));
      setCompanies(c);
    } catch {
      toast.error("Konnte fehlende Belege nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [companyFilter]);

  const filtered = items.filter(
    (t) =>
      !filter ||
      (t.description || "").toLowerCase().includes(filter.toLowerCase()) ||
      (t.counterparty || "").toLowerCase().includes(filter.toLowerCase())
  );

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.filter((t) => !t.pending_request).map((t) => t.id)));
  };

  const cancelReq = async (rid) => {
    try {
      await api.delete(`/receipt-requests/${rid}`);
      toast.success("Anfrage abgebrochen");
      load();
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  const sendBulk = async () => {
    if (!bulkMember || selected.size === 0) return;
    let ok = 0, undelivered = 0, fail = 0;
    for (const id of selected) {
      try {
        const { data } = await api.post("/receipt-requests", {
          transaction_id: id, member_id: bulkMember, note: bulkNote || null,
        });
        if (data?.telegram_delivered === false) undelivered += 1;
        else ok += 1;
      } catch { fail += 1; }
    }
    const parts = [];
    if (ok) parts.push(`${ok} gesendet`);
    if (undelivered) parts.push(`${undelivered} gespeichert (nicht zugestellt)`);
    if (fail) parts.push(`${fail} fehlgeschlagen`);
    (fail ? toast.error : undelivered ? toast.warning : toast.success)(parts.join(" · "));
    setShowBulk(false);
    setSelected(new Set());
    setBulkNote("");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Fehlende Belege"
        subtitle="Alle offenen Buchungen ohne zugeordneten Beleg – schnell per Telegram anfragen."
        actions={
          selected.size > 0 && (
            <PrimaryButton onClick={() => { setBulkMember(team[0]?.id || ""); setShowBulk(true); }} data-testid="bulk-request-btn">
              <Send className="h-4 w-4" /> {selected.size} anfragen
            </PrimaryButton>
          )
        }
      />

      <div className="px-8 py-8 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Suchen…"
            data-testid="missing-filter"
            className="px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] flex-1 min-w-[240px]"
          />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            data-testid="missing-company-filter"
            className="px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white"
          >
            <option value="">Alle Firmen</option>
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E6E6E4] rounded-md p-8 text-center text-sm text-[#5C5E60]">
            Alle Buchungen sind bereits mit einem Beleg verknüpft. 🎉
          </div>
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="missing-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      onChange={toggleAll}
                      checked={selected.size > 0 && selected.size === filtered.filter((t) => !t.pending_request).length}
                      data-testid="missing-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold">Datum</th>
                  <th className="text-left px-4 py-3 font-semibold">Buchung</th>
                  <th className="text-left px-4 py-3 font-semibold">Firma</th>
                  <th className="text-right px-4 py-3 font-semibold">Betrag</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-t border-[#E6E6E4] hover:bg-[#F9F9F8]">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        disabled={!!t.pending_request}
                        onChange={() => toggle(t.id)}
                        data-testid={`select-${t.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono-tab text-[#1A1A1A]">{fmtDate(t.booking_date)}</td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-[#1A1A1A] truncate">{t.description}</div>
                      {t.counterparty && <div className="text-xs text-[#5C5E60] truncate">{t.counterparty}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {t.company_name ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: t.company_color || "#18322B" }} />
                          {t.company_name}
                        </div>
                      ) : (
                        <span className="text-[#5C5E60]">—</span>
                      )}
                    </td>
                    <td className={`text-right px-4 py-3 font-mono-tab font-semibold ${t.amount < 0 ? "text-[#B31D1D]" : "text-[#1E4620]"}`}>
                      {fmtEUR(t.amount)}
                    </td>
                    <td className="px-4 py-3">
                      {t.pending_request ? (
                        <StatusBadge variant="info">Anfrage läuft</StatusBadge>
                      ) : (
                        <StatusBadge variant="warning">Offen</StatusBadge>
                      )}
                    </td>
                    <td className="text-right px-6 py-3">
                      <div className="inline-flex items-center gap-1">
                        {t.pending_request ? (
                          <button
                            onClick={() => cancelReq(t.pending_request.id)}
                            data-testid={`missing-cancel-${t.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D]">
                            <X className="h-3.5 w-3.5" /> Stoppen
                          </button>
                        ) : null}
                        {t.statement_id && (
                          <Link to={`/app/kontoauszuege/${t.statement_id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-[#F3F3F1] text-[#5C5E60]">
                            <ExternalLink className="h-3.5 w-3.5" /> Auszug
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="bulk-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">{selected.size} Anfragen senden</div>
              <button onClick={() => setShowBulk(false)} className="text-[#5C5E60]"><X className="h-4 w-4" /></button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Mitarbeiter</label>
              <select
                value={bulkMember}
                onChange={(e) => setBulkMember(e.target.value)}
                data-testid="bulk-member"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm">
                {team.length === 0 && <option value="">Kein Mitarbeiter mit Telegram-Verknüpfung</option>}
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email}{m.telegram_username ? ` (@${m.telegram_username})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Notiz (optional)</label>
              <textarea
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                data-testid="bulk-note"
                rows={2}
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm" />
            </div>

            <div className="flex justify-end gap-2">
              <SecondaryButton onClick={() => setShowBulk(false)}>Abbrechen</SecondaryButton>
              <PrimaryButton onClick={sendBulk} disabled={!bulkMember} data-testid="bulk-submit">
                <Send className="h-4 w-4" /> Senden
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
